/**
 * Approved ElevenLabs voice IDs — single source of truth for HeirWise.
 *
 * Any voice generation, playback, or admin configuration MUST use these IDs.
 * Legacy IDs (including older Lumi IDs) are deliberately absent. If an audio
 * asset references a voice not listed here, treat it as unapproved.
 */
export const APPROVED_VOICE_IDS = {
  aelyra_solari: "kPzsL2i3teMYv0FxEYQ6",
  caelion_veyr: "UGTtbzgh3HObxRjWaSpr",
  headmaster_vaelor: "qxePw1S1QmBgjlU3GIy5",
  lumi_vanta: "KR1TkIhkSykEjI4B0DtH",
} as const;

export type ApprovedVoiceKey = keyof typeof APPROVED_VOICE_IDS;
export type ApprovedVoiceId = (typeof APPROVED_VOICE_IDS)[ApprovedVoiceKey];

const APPROVED_ID_SET = new Set<string>(Object.values(APPROVED_VOICE_IDS));

export function isApprovedVoiceId(id: string | null | undefined): id is ApprovedVoiceId {
  return !!id && APPROVED_ID_SET.has(id);
}

/** Current Lumi voice ID. Any Lumi voice ID that is not this value is legacy. */
export const CURRENT_LUMI_VOICE_ID = APPROVED_VOICE_IDS.lumi_vanta;

/**
 * Client-side instructor slug → approved ElevenLabs voice ID map.
 * Server (edge function `instructor-voice`) remains the ultimate source of
 * truth for playback voice selection. This map exists so client playback
 * can refuse to request a signed URL when an instructor slug does not
 * correspond to an approved voice, providing defense in depth.
 */
export const INSTRUCTOR_APPROVED_VOICE: Record<string, ApprovedVoiceId> = {
  aelyra: APPROVED_VOICE_IDS.aelyra_solari,
  caelion: APPROVED_VOICE_IDS.caelion_veyr,
  vaelor: APPROVED_VOICE_IDS.headmaster_vaelor,
  lumi: APPROVED_VOICE_IDS.lumi_vanta,
};

export function approvedVoiceIdForInstructor(
  instructor: string | null | undefined,
): ApprovedVoiceId | null {
  if (!instructor) return null;
  return INSTRUCTOR_APPROVED_VOICE[instructor] ?? null;
}

/** True when the instructor slug maps to an approved voice ID. */
export function isApprovedInstructor(instructor: string | null | undefined): boolean {
  return isApprovedVoiceId(approvedVoiceIdForInstructor(instructor));
}